import React from 'react';
import { useQuery } from 'react-query';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { getIncentives } from 'apis/CommunityAPI';
import BorderedStyledTable from 'common/Table/BorderedStyledTable';
import ErrorMessage from 'components/lib/ErrorMessage';
import { LIVING_TYPES_LABELS } from 'constants/livingTypes';
import { CommunityIncentiveType } from 'types/incentives';

const StyledTable = styled(BorderedStyledTable)`
  &&& {
    .ant-table-tbody > tr.highlighted-incentive > td {
      background: rgba(0, 122, 255, 0.05) !important;
    }
  }
`;

const IncentiveCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IncentiveText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IncentiveTitle = styled.div`
  font-size: 14px;
  font-family: var(--font-bold);
  color: var(--text-primary);
`;

const ToggleText = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 12px;
  color: var(--dark-blue);
  cursor: pointer;
  text-align: left;
  display: inline;
`;

const CompetitorName = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const CareTypeText = styled.div`
  font-size: 14px;
  color: var(--text-primary);
`;

const AddedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AddedDate = styled.span`
  font-size: 14px;
  color: var(--text-primary);
`;

const AddedTime = styled.span`
  font-size: 12px;
  color: var(--text-secondary);
`;

const AttachmentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const AttachmentRow = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--dark-blue);
  font-size: 12px;
`;

type Props = {
  communityId: number;
  highlightedIncentiveId?: number | null;
};

const TITLE_COLLAPSE_THRESHOLD = 160;

const getCareTypeLabel = (item: CommunityIncentiveType & any) => {
  if (Array.isArray(item?.care_types) && item.care_types.length > 0) {
    return item.care_types.join(', ');
  }

  if (Array.isArray(item?.living_types) && item.living_types.length > 0) {
    return item.living_types
      .map((livingType: number) => LIVING_TYPES_LABELS[livingType] || '')
      .filter(Boolean)
      .join(', ');
  }

  return '-';
};

type IncentiveTitleCellProps = {
  text: string;
};

const IncentiveTitleCell = ({ text }: IncentiveTitleCellProps) => {
  const [expanded, setExpanded] = React.useState(false);
  const shouldToggle = text.length > TITLE_COLLAPSE_THRESHOLD;
  const collapsedText = shouldToggle ? `${text.slice(0, TITLE_COLLAPSE_THRESHOLD)}...` : text;

  return (
    <IncentiveTitle>
      {shouldToggle && !expanded ? collapsedText : text}{' '}
      {shouldToggle && (
        <ToggleText type="button" onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Show less' : 'Show more'}
        </ToggleText>
      )}
    </IncentiveTitle>
  );
};

const buildColumns = (showCompetitor: boolean) => [
  ...(showCompetitor
    ? [
        {
          title: 'Competitor',
          width: '18%',
          key: 'competitor',
          render: (_value: any, record: CommunityIncentiveType & any) => (
            <CompetitorName>{record?.community_name || '-'}</CompetitorName>
          ),
        },
      ]
    : []),
  {
    title: 'Incentive',
    width: showCompetitor ? '30%' : '34%',
    key: 'incentive',
    render: (_value: any, record: CommunityIncentiveType & any) => (
      <IncentiveCell>
        <img src="/icons/price-tag-green.svg" alt="incentive" width={16} height={16} />
        <IncentiveText>
          <IncentiveTitleCell text={record?.description || ''} />
        </IncentiveText>
      </IncentiveCell>
    ),
  },
  {
    title: 'Care Type',
    width: showCompetitor ? '18%' : '22%',
    key: 'care-type',
    render: (_value: any, record: CommunityIncentiveType & any) => (
      <CareTypeText>{getCareTypeLabel(record)}</CareTypeText>
    ),
  },
  {
    title: 'Attachments',
    width: showCompetitor ? '19%' : '24%',
    key: 'attachments',
    render: (_value: any, record: CommunityIncentiveType & any) => {
      const attachments = [...(record?.files || []), ...(record?.images || [])];
      if (!attachments.length) return '-';

      return (
        <AttachmentsList>
          {attachments.map((attachment: any) => (
            <AttachmentRow key={attachment.id} href={attachment.path} target="_blank" rel="noopener noreferrer">
              <img alt="attachment" src="/icons/attachment.svg" width={12} height={12} />
              <span>
                {attachment.file_name}.{attachment.file_extension}
              </span>
            </AttachmentRow>
          ))}
        </AttachmentsList>
      );
    },
  },
  {
    title: 'Added',
    width: showCompetitor ? '15%' : '20%',
    key: 'added',
    render: (_value: any, record: CommunityIncentiveType & any) => {
      const addedAt = record?.date || record?.created_at;
      return (
        <AddedCell>
          <AddedDate>{addedAt ? dayjs(addedAt).format('MMM D YYYY') : '-'}</AddedDate>
          <AddedTime>{addedAt ? dayjs(addedAt).format('h:mm A') : ''}</AddedTime>
        </AddedCell>
      );
    },
  },
];

type IncentivesTableProps = {
  rows: (CommunityIncentiveType & any)[];
  loading?: boolean;
  showCompetitor?: boolean;
  highlightedIncentiveId?: number | null;
};

export const IncentivesTable = ({
  rows,
  loading,
  showCompetitor = false,
  highlightedIncentiveId = null,
}: IncentivesTableProps) => (
  <StyledTable
    columns={buildColumns(showCompetitor) as any}
    dataSource={rows}
    loading={loading}
    rowKey={(record: any) => record?.id}
    rowClassName={(record: any) =>
      highlightedIncentiveId && record?.id === highlightedIncentiveId ? 'highlighted-incentive' : ''
    }
    pagination={false}
    showSorterTooltip={false}
  />
);

const AlertsIncentivesTable = ({ communityId, highlightedIncentiveId = null }: Props) => {
  const {
    data = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['alert-details-incentives', communityId],
    queryFn: () =>
      getIncentives(communityId || -1, {
        status: 1,
        competitors: [communityId],
        care_types: [],
      }),
    enabled: Boolean(communityId),
    refetchOnWindowFocus: false,
  });

  return (
    <>
      <ErrorMessage text="Failed to load incentives. Please try again." active={isError} />
      <IncentivesTable rows={data} loading={isLoading} highlightedIncentiveId={highlightedIncentiveId} />
    </>
  );
};

export default AlertsIncentivesTable;
